import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { MatTooltipModule } from '@angular/material/tooltip';
import { BasePackageGridCardDirective } from '../../shared/components/base-package-grid-card.directive';
import { PackageGridCardFeaturesComponent } from '../../shared/components/package-grid-card-features/package-grid-card-features.component';
import { ThemeBackgroundPipe } from '../../shared/pipes/theme-background.pipe';
import { getIsMobileOrTablet } from '../../shared/utils/device.util';

/**
 * Displays a single package as a card within a package grid and exposes actions to edit, delete, or duplicate the package.
 * @component
 * @selector app-package-grid-card
 * @example
 *   <app-package-grid-card [package]="samplePackage" (edit)="onEdit($event)" (delete)="onDelete($event)" (duplicate)="onDuplicate($event)"></app-package-grid-card>
 * @input any package - The package object to render inside the card (typically provided by the parent grid). This input is provided by the BasePackageGridCardDirective which this component extends.
 * @output EventEmitter<any> edit - Emitted when the user invokes the edit action for the package; payload is the package object.
 * @output EventEmitter<any> delete - Emitted when the user invokes the delete action for the package; payload is the package object.
 * @output EventEmitter<any> duplicate - Emitted when the user invokes the duplicate action for the package; payload is the package object.
 *
 * Key methods:
 * - editPackage(): Emits the edit event with the current package.
 * - deletePackage(): Emits the delete event with the current package.
 * - duplicatePackage(): Emits the duplicate event with the current package.
 *
 * Important properties and getters:
 * - isMobileOrTablet: boolean getter that returns true when the UI is running on a mobile or tablet device (used for responsive/touch behavior).
 * - isTouch: boolean getter that mirrors isMobileOrTablet and is used to determine touch-specific UI behavior.
 *
 * Lifecycle and behavior notes:
 * - The component is standalone and imports common UI utilities (CommonModule and MatTooltipModule).
 * - It extends BasePackageGridCardDirective and relies on that base class for shared package-related inputs and logic.
 * - A NotificationService is injected via the constructor and passed to the base directive; no additional Angular lifecycle hooks are implemented directly in this class.
 */
@Component({
  selector: 'app-package-grid-card',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, PackageGridCardFeaturesComponent, ThemeBackgroundPipe],
  templateUrl: './package-grid-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-grid-card.component.scss'],
})
export class PackageGridCardComponent extends BasePackageGridCardDirective {
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() duplicate = new EventEmitter<any>();

  constructor(messageService: NotificationService) {
    super(messageService);
  }

  editPackage(): void {
    this.edit.emit(this.package);
  }

  deletePackage(): void {
    this.delete.emit(this.package);
  }

  duplicatePackage(): void {
    this.duplicate.emit(this.package);
  }

  get isMobileOrTablet(): boolean {
    return getIsMobileOrTablet();
  }

  get isTouch(): boolean {
    return this.isMobileOrTablet;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
